// ============================================
// Login Page
// ============================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { functions } from '../config/firebase';

// Superadmin emails that can self-promote
const SUPERADMIN_EMAILS = ['organizer@pokernm.com'];

export default function Login() {
  const { user, role, loading, error, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settingUpRole, setSettingUpRole] = useState(false);
  const [setupError, setSetupError] = useState<string | null>(null);

  React.useEffect(() => {
    const setupSuperadmin = async () => {
      if (user && !loading && !role && user.email && SUPERADMIN_EMAILS.includes(user.email)) {
        // User is in superadmin allowlist but has no role - set it up
        setSettingUpRole(true);
        setSetupError(null);
        try {
          const setUserRole = httpsCallable(functions, 'setUserRole');
          await setUserRole({ uid: user.uid, role: 'superadmin' });
          // Force token refresh to get new claims
          await user.getIdToken(true);
          // Reload the page to pick up new claims
          window.location.reload();
        } catch (err: any) {
          console.error('Error setting superadmin role:', err);
          setSetupError(err.message || 'Failed to set up admin role');
          setSettingUpRole(false);
        }
      }
    };

    if (user && !loading) {
      if (!role && user.email && SUPERADMIN_EMAILS.includes(user.email)) {
        setupSuperadmin();
      } else {
        // Redirect based on role
        const from = (location.state as any)?.from?.pathname;
        if (from) {
          navigate(from, { replace: true });
        } else if (role === 'superadmin' || role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (role === 'therapist') {
          navigate('/t', { replace: true });
        }
      }
    }
  }, [user, role, loading, navigate, location]);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (err) {
      // Error is handled in context
    }
  };

  if (loading || settingUpRole) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="loading">
            {settingUpRole ? 'Setting up admin access...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>Poker Massage</h1>
          <p>Staff Login</p>
        </div>

        {(error || setupError) && (
          <div className="error-message">{error || setupError}</div>
        )}

        <button className="btn btn-google" onClick={handleGoogleSignIn}>
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </button>

        <div className="login-footer">
          <p>For therapists and administrators only</p>
          <p className="small">Players access via event QR code</p>
        </div>
      </div>
    </div>
  );
}
